import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  Vec2,
  SimulationParameters,
  ConstraintConfig,
  TrajectoryMode,
  ParametricTrajectory,
  ColorSettings,
} from '@/types';
import type { PlotMode, ParameterStudyConfig, ParameterStudyResult } from '@/formula';
import { DEFAULT_COLOR_SETTINGS } from '@/types/colors';
import { PRESETS } from '@/utils/presets';
import {
  createExpressionEvaluator,
  computeBoundaryPolygon,
} from '@/shapes/expressionConstraint';

interface SimulationStore {
  // Parameters
  params: SimulationParameters;
  setParams: (params: Partial<SimulationParameters>) => void;

  // Constraint
  constraint: ConstraintConfig;
  setConstraint: (constraint: ConstraintConfig) => void;

  // Constraint angle (controlled by mouse wheel)
  constraintAngle: number;
  setConstraintAngle: (angle: number) => void;

  // Precomputed boundary polygon (in local coordinates)
  boundaryPolygon: Vec2[];
  recomputeBoundary: () => void;

  // Trajectory mode
  trajectoryMode: TrajectoryMode;
  setTrajectoryMode: (mode: TrajectoryMode) => void;

  // Parametric trajectory settings
  parametricTrajectory: ParametricTrajectory;
  setParametricTrajectory: (traj: ParametricTrajectory) => void;

  // Free-drag position (for free-drag mode)
  dragPosition: Vec2;
  setDragPosition: (pos: Vec2) => void;

  // Simulation state (delayed sweeping)
  isRunning: boolean;
  currentStep: number;
  trajectory: Vec2[];
  preProjection: Vec2[];
  constraintCenters: Vec2[];
  constraintAngles: number[];
  projectionDistances: number[];
  gradientNorms: number[];

  // Classical sweeping state
  classicalTrajectory: Vec2[];
  classicalGradientNorms: number[];

  setRunning: (running: boolean) => void;
  setCurrentStep: (step: number) => void;
  appendTrajectoryPoint: (
    point: Vec2,
    xBar: Vec2,
    center: Vec2,
    projDist: number,
    gradNorm: number
  ) => void;
  appendClassicalPoint: (point: Vec2, gradNorm: number) => void;
  resetTrajectory: () => void;

  // History navigation
  viewStep: number;
  setViewStep: (step: number) => void;

  // Color settings (includes past constraints color config)
  colorConfig: ColorSettings;
  setColorConfig: (partial: Partial<ColorSettings>) => void;

  // Past constraints visibility and times
  pastConstraintTimes: number[];
  showPastConstraints: boolean;
  setPastConstraintTimes: (times: number[]) => void;
  setShowPastConstraints: (show: boolean) => void;

  // UI state
  speed: number;
  setSpeed: (speed: number) => void;

  // Formula plotting
  formula: string;
  plotMode: PlotMode;
  showPlot: boolean;
  parameterStudyConfig: ParameterStudyConfig;
  parameterStudyResults: ParameterStudyResult[];
  parameterStudyRunning: boolean;
  parameterStudyProgress: number;
  setFormula: (formula: string) => void;
  setPlotMode: (mode: PlotMode) => void;
  setShowPlot: (show: boolean) => void;
  setParameterStudyConfig: (config: ParameterStudyConfig) => void;
  setParameterStudyResults: (results: ParameterStudyResult[]) => void;
  setParameterStudyRunning: (running: boolean) => void;
  setParameterStudyProgress: (progress: number) => void;

  // Presets
  loadPreset: (presetId: string) => void;

  // Persistence
  saveToLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  exportToJSON: () => string;
  importFromJSON: (json: string) => boolean;
  applySharedState: (data: Record<string, unknown>) => void;
}

const DEFAULT_PARAMS: SimulationParameters = {
  T: 15.0,
  h: 0.005,
  epsilon: 2.5,
  infiniteMode: true,
  xPastExpression: '0',
  yPastExpression: '0',
  solverType: 'norm1-sum1',
  projectionTolerance: 1e-8,
};

const DEFAULT_CONSTRAINT: ConstraintConfig = {
  expression: 'R - sqrt(x^2 + y^2)',
  R: 0.5,
  r: 0.3,
  a: 0,
  b: 0,
};

// Helper to compute boundary from constraint config
function computeBoundaryFromConfig(config: ConstraintConfig): Vec2[] {
  try {
    const evaluator = createExpressionEvaluator(config.expression, {
      R: config.R,
      r: config.r,
      a: config.a,
      b: config.b,
    });
    return computeBoundaryPolygon(evaluator);
  } catch {
    // Return empty polygon on error
    return [];
  }
}

// Helper to generate default past constraint times: 0:(T/6):T
function generateDefaultPastConstraintTimes(T: number): number[] {
  const step = T / 6;
  const times: number[] = [];
  for (let t = 0; t <= T + 1e-10; t += step) {
    times.push(t);
  }
  return times;
}

const DEFAULT_TRAJECTORY: ParametricTrajectory = {
  xExpression: '2 * sin(t)',
  yExpression: '2 * sin(2*t)',
  alphaExpression: '0',
};

export const useSimulationStore = create<SimulationStore>()(
  subscribeWithSelector((set, get) => ({
    // Default values
    params: DEFAULT_PARAMS,
    constraint: DEFAULT_CONSTRAINT,
    constraintAngle: 0,
    boundaryPolygon: computeBoundaryFromConfig(DEFAULT_CONSTRAINT),
    trajectoryMode: 'parametric',
    parametricTrajectory: DEFAULT_TRAJECTORY,
    dragPosition: { x: 0, y: 0 },

    isRunning: false,
    currentStep: 0,
    trajectory: [],
    preProjection: [],
    constraintCenters: [],
    constraintAngles: [],
    projectionDistances: [],
    gradientNorms: [],
    classicalTrajectory: [],
    classicalGradientNorms: [],

    // History navigation
    viewStep: 0,

    // Color settings
    colorConfig: DEFAULT_COLOR_SETTINGS,

    // Past constraints
    pastConstraintTimes: generateDefaultPastConstraintTimes(DEFAULT_PARAMS.T),
    showPastConstraints: true,

    speed: 1,

    // Formula plotting defaults
    formula: 'g[n]',
    plotMode: 'instantaneous' as PlotMode,
    showPlot: true,
    parameterStudyConfig: {
      parameter: 'epsilon' as const,
      min: 0.5,
      max: 10,
      sampleCount: 8,
      scalingMode: 'linear' as const,
      expBase: 2,
      expMin: -8,
      expMax: -4,
      expStep: 1,
      aggregation: 'final' as const,
      logXAxis: false,
      logYAxis: false,
      convergenceRefMode: 'finest' as const,
      convergenceRefValue: -10,
    },
    parameterStudyResults: [],
    parameterStudyRunning: false,
    parameterStudyProgress: 0,

    // Actions
    setParams: (params) =>
      set((state) => ({
        params: { ...state.params, ...params },
      })),

    setConstraint: (constraint) => {
      const boundaryPolygon = computeBoundaryFromConfig(constraint);
      set({ constraint, boundaryPolygon });
    },

    setConstraintAngle: (angle) => set({ constraintAngle: angle }),

    recomputeBoundary: () => {
      const { constraint } = get();
      const boundaryPolygon = computeBoundaryFromConfig(constraint);
      set({ boundaryPolygon });
    },

    setTrajectoryMode: (mode) => set({ trajectoryMode: mode }),

    setParametricTrajectory: (traj) => set({ parametricTrajectory: traj }),

    setDragPosition: (pos) => set({ dragPosition: pos }),

    setRunning: (running) => set({ isRunning: running }),

    setCurrentStep: (step) => set({ currentStep: step }),

    appendTrajectoryPoint: (point, xBar, center, projDist, gradNorm) => {
      const state = get();
      state.trajectory.push(point);
      state.preProjection.push(xBar);
      state.constraintCenters.push(center);
      state.constraintAngles.push(state.constraintAngle);
      state.projectionDistances.push(projDist);
      state.gradientNorms.push(gradNorm);
      set({
        currentStep: state.currentStep + 1,
        viewStep: state.trajectory.length, // array already has the new element
      });
    },

    appendClassicalPoint: (point, gradNorm) => {
      // Mutate in-place; re-render is triggered by appendTrajectoryPoint's
      // viewStep update which is always called in the same simulation step.
      const state = get();
      state.classicalTrajectory.push(point);
      state.classicalGradientNorms.push(gradNorm);
    },

    resetTrajectory: () =>
      set({
        trajectory: [],
        preProjection: [],
        constraintCenters: [],
        constraintAngles: [],
        projectionDistances: [],
        gradientNorms: [],
        classicalTrajectory: [],
        classicalGradientNorms: [],
        currentStep: 0,
        viewStep: 0,
        isRunning: false,
      }),

    setViewStep: (step) => set({ viewStep: step }),

    setColorConfig: (partial) =>
      set((state) => ({
        colorConfig: { ...state.colorConfig, ...partial },
      })),

    setPastConstraintTimes: (times) => set({ pastConstraintTimes: times }),
    setShowPastConstraints: (show) => set({ showPastConstraints: show }),

    setSpeed: (speed) => set({ speed: Math.max(1, Math.min(100, speed)) }),

    // Formula plotting setters
    setFormula: (formula) => set({ formula }),
    setPlotMode: (plotMode) => set({ plotMode }),
    setShowPlot: (showPlot) => set({ showPlot }),
    setParameterStudyConfig: (parameterStudyConfig) => set({ parameterStudyConfig }),
    setParameterStudyResults: (parameterStudyResults) => set({ parameterStudyResults }),
    setParameterStudyRunning: (parameterStudyRunning) => set({ parameterStudyRunning }),
    setParameterStudyProgress: (parameterStudyProgress) => set({ parameterStudyProgress }),

    loadPreset: (presetId) => {
      const preset = PRESETS.find((p) => p.id === presetId);
      if (preset) {
        const boundaryPolygon = computeBoundaryFromConfig(preset.constraint);
        set({
          params: { ...preset.params },
          constraint: { ...preset.constraint },
          boundaryPolygon,
          constraintAngle: 0,
          parametricTrajectory: { ...preset.trajectory },
          trajectoryMode: 'parametric',
          // Reset trajectory state
          trajectory: [],
          preProjection: [],
          constraintCenters: [],
          constraintAngles: [],
          projectionDistances: [],
          gradientNorms: [],
          classicalTrajectory: [],
          classicalGradientNorms: [],
          currentStep: 0,
          viewStep: 0,
          isRunning: false,
        });
      }
    },

    saveToLocalStorage: () => {
      const state = get();
      const data = {
        params: state.params,
        constraint: state.constraint,
        constraintAngle: state.constraintAngle,
        trajectoryMode: state.trajectoryMode,
        parametricTrajectory: state.parametricTrajectory,
        speed: state.speed,
        colorConfig: state.colorConfig,
        formula: state.formula,
        plotMode: state.plotMode,
        parameterStudyConfig: state.parameterStudyConfig,
      };
      try {
        localStorage.setItem('websim-params', JSON.stringify(data));
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    },

    loadFromLocalStorage: () => {
      try {
        const stored = localStorage.getItem('websim-params');
        if (stored) {
          const data = JSON.parse(stored);
          // Migrate old constraint format if needed
          let constraint = data.constraint;
          if (constraint && !constraint.expression) {
            constraint = {
              ...DEFAULT_CONSTRAINT,
              R: constraint.radius ?? DEFAULT_CONSTRAINT.R,
            };
          }
          constraint = constraint ?? DEFAULT_CONSTRAINT;

          // Migrate params: ensure solverType is set
          const params = {
            ...DEFAULT_PARAMS,
            ...(data.params ?? {}),
            solverType: data.params?.solverType ?? 'norm1-sum1',
          };

          const boundaryPolygon = computeBoundaryFromConfig(constraint);
          set({
            params,
            constraint,
            boundaryPolygon,
            constraintAngle: data.constraintAngle ?? 0,
            trajectoryMode: data.trajectoryMode ?? 'parametric',
            parametricTrajectory: data.parametricTrajectory ?? DEFAULT_TRAJECTORY,
            speed: data.speed ?? 1,
            colorConfig: data.colorConfig ?? DEFAULT_COLOR_SETTINGS,
            formula: data.formula ?? 'g[n]',
            plotMode: data.plotMode ?? 'instantaneous',
            parameterStudyConfig: {
              parameter: 'epsilon',
              min: 0.5,
              max: 10,
              sampleCount: 8,
              scalingMode: 'linear',
              expBase: 2,
              expMin: -8,
              expMax: -4,
              expStep: 1,
              aggregation: 'final',
              logXAxis: false,
              logYAxis: false,
              convergenceRefMode: 'finest',
              convergenceRefValue: -10,
              ...(data.parameterStudyConfig ?? {}),
            },
          });
        }
      } catch (e) {
        console.error('Failed to load from localStorage:', e);
      }
    },

    exportToJSON: () => {
      const state = get();
      return JSON.stringify(
        {
          params: state.params,
          constraint: state.constraint,
          trajectoryMode: state.trajectoryMode,
          parametricTrajectory: state.parametricTrajectory,
        },
        null,
        2
      );
    },

    importFromJSON: (json) => {
      try {
        const data = JSON.parse(json);
        let constraint = data.constraint;
        if (constraint && !constraint.expression) {
          constraint = {
            ...DEFAULT_CONSTRAINT,
            R: constraint.radius ?? DEFAULT_CONSTRAINT.R,
          };
        }
        constraint = constraint ?? DEFAULT_CONSTRAINT;

        const params = {
          ...DEFAULT_PARAMS,
          ...(data.params ?? {}),
          solverType: data.params?.solverType ?? 'norm1-sum1',
        };

        const boundaryPolygon = computeBoundaryFromConfig(constraint);
        set({
          params,
          constraint,
          boundaryPolygon,
          constraintAngle: data.constraintAngle ?? 0,
          trajectoryMode: data.trajectoryMode ?? 'parametric',
          parametricTrajectory: data.parametricTrajectory ?? DEFAULT_TRAJECTORY,
        });
        return true;
      } catch (e) {
        console.error('Failed to import JSON:', e);
        return false;
      }
    },

    applySharedState: (data: Record<string, unknown>) => {
      // Reuses same migration logic as loadFromLocalStorage
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d = data as any;
      let constraint: ConstraintConfig = d.constraint ?? DEFAULT_CONSTRAINT;
      if (constraint && !constraint.expression) {
        constraint = {
          ...DEFAULT_CONSTRAINT,
          R: (constraint as any).radius ?? DEFAULT_CONSTRAINT.R,
        };
      }

      const params: SimulationParameters = {
        ...DEFAULT_PARAMS,
        ...(d.params ?? {}),
        solverType: d.params?.solverType ?? 'norm1-sum1',
      };

      const boundaryPolygon = computeBoundaryFromConfig(constraint);
      set({
        params,
        constraint,
        boundaryPolygon,
        constraintAngle: d.constraintAngle ?? 0,
        trajectoryMode: d.trajectoryMode ?? 'parametric',
        parametricTrajectory: d.parametricTrajectory ?? DEFAULT_TRAJECTORY,
        speed: d.speed ?? 1,
        colorConfig: d.colorConfig ?? DEFAULT_COLOR_SETTINGS,
        formula: d.formula ?? 'g[n]',
        plotMode: d.plotMode ?? 'instantaneous',
        showPlot: d.showPlot ?? true,
        parameterStudyConfig: {
          parameter: 'epsilon' as const,
          min: 0.5,
          max: 10,
          sampleCount: 8,
          scalingMode: 'linear' as const,
          expBase: 2,
          expMin: -8,
          expMax: -4,
          expStep: 1,
          aggregation: 'final' as const,
          logXAxis: false,
          logYAxis: false,
          convergenceRefMode: 'finest' as const,
          convergenceRefValue: -10,
          ...(d.parameterStudyConfig ?? {}),
        },
        parameterStudyResults: d.parameterStudyResults ?? [],
      });
    },
  }))
);
